import Modal from '@/components/Modal';
import ProductSizes from '@/components/ProductSizes';
import { RVI_LS_PROPERTY_NAME, base64Image, dataLayer } from '@/constants';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { useAddToCartMutation, useCart } from '@/hooks/cart';
import { useElementOnScreen } from '@/hooks/intersectionObserver';
import { useWishlistProducts } from '@/hooks/products';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { useAddItemToWishlist, useDeleteItemFromWishlist } from '@/hooks/wishlistHooks';
import { IconArrowUp } from '@/icons/IconArrowUp';
import { IconCare } from '@/icons/IconCare';
import { IconFire } from '@/icons/IconFire';
import { IconHeart } from '@/icons/IconHeart';
import { IconInfoGraphic } from '@/icons/IconInfoGraphic';
import { IconMinus } from '@/icons/IconMinus';
import { IconPlus } from '@/icons/IconPlus';
import { IconScale } from '@/icons/IconScale';
import { IconShoppingBag } from '@/icons/IconShoppingBag';
import { ICartItems } from '@/interfaces/cart';
import { IProductDetails } from '@/interfaces/product';
import useHeaderStore from '@/store/header';
import { numberFormat } from '@/utils/helper';
import { Tooltip } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import FloatingCart from './FloatingCart';
import { PageHeading } from './PageHeading';

const ProductOptions = (props: {
  product: IProductDetails;
  quickView: boolean;
  productInfoGraphic?: number;
  setProductCardSeName?: (value: string) => void;
}) => {
  const { product, quickView, productInfoGraphic, setProductCardSeName } = props;

  const router = useRouter();
  const queryClient = useQueryClient();
  const authObject = useReactQuerySession();
  const [productSize, selectProductSize] = useState<number>(product?.IsGiftVoucher ? product?.Variations[0]?.ProductAttributeValue : -1);
  const [productQuantity, setProductQuantity] = useState<number>(1);
  const [sizeModal, setSizeModal] = useState<any>(false);
  const [careModal, setCareModal] = useState<any>(false);
  const [isBuyNow, setIsBuyNow] = useState<boolean>(false);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const { mutate: mutateAddToCart } = useAddToCartMutation(addToCartCallback);
  const { mutate: mutateAddToWishlist } = useAddItemToWishlist(true, product?.SeName, toggleProductWishlistState);
  const { mutate: deleteFromWishlist } = useDeleteItemFromWishlist(true, product?.SeName, toggleProductWishlistState);
  const [scrolledDownFormAddToCart, setScrolledDownFormAddToCart] = useState(false);
  const { data: cartData, isLoading: cartDataLoading } = useCart(queryClient, authObject?.session);
  const { data: wishListProduct } = useWishlistProducts(product?.SeName, authObject?.session, authObject?.isAuthenticated);

  const { openCartPanel, cartPanel } = useHeaderStore();

  useEffect(() => {
    if (router.isReady) {
      let stringifiedRVIs = localStorage.getItem(RVI_LS_PROPERTY_NAME);
      if (stringifiedRVIs) {
        try {
          let arrayOfProductIds = JSON.parse(stringifiedRVIs);
          if (Array.isArray(arrayOfProductIds)) {
            //arrayOfProductIds is a valid array
            let typedArrayOfProductIds = arrayOfProductIds as string[];
            const indexOfFoundSeName = typedArrayOfProductIds.findIndex((x) => x === product.Id);
            if (indexOfFoundSeName > -1) {
              typedArrayOfProductIds.splice(indexOfFoundSeName, 1);
            }
            typedArrayOfProductIds.unshift(product.Id);
            if (typedArrayOfProductIds.length > 15) {
              // trimming and keeping hte latest 15 visited product Ids only
              typedArrayOfProductIds.length = 15;
            }
            localStorage.setItem(RVI_LS_PROPERTY_NAME, JSON.stringify(typedArrayOfProductIds));
          } else {
            localStorage.setItem(RVI_LS_PROPERTY_NAME, JSON.stringify([product.Id]));
          }
        } catch (e) {
          localStorage.setItem(RVI_LS_PROPERTY_NAME, JSON.stringify([product.Id]));
        }
      } else {
        localStorage.setItem(RVI_LS_PROPERTY_NAME, JSON.stringify([product.Id]));
      }
    }
  }, [router.asPath, router.isReady]);

  function addToCartCallback(shouldOpenCart: boolean = true, shouldOpenNotification: boolean = true) {
    if (shouldOpenCart) {
      openCartPanel(true);
    }
    if (shouldOpenNotification) {
      openNotification('success', 'Cart', 'Product added to cart successfully');
    }
    if (isBuyNow) {
      router.push('/checkout');
      setIsBuyNow(false);
    }

    // selectProductSize(-1);
    setProductQuantity(1);
  }

  function toggleProductWishlistState(IsWishListed: boolean, productId: string) {
    product.IsWishListed = IsWishListed;
    queryClient.setQueryData([product.SeName], () => {
      return { ...product };
    });
  }

  const handleWishlistClick = () => {
    if (!wishListProduct?.IsWishListed) {
      mutateAddToWishlist({ productId: product.Id });
    } else {
      deleteFromWishlist(product.Id);
    }
  };

  const addToCart = (product: IProductDetails, shouldOpenCart: boolean = true, shouldOpenNotification: boolean = true) => {
    let tempSelectedSize = product.Variations?.find((x) => x.ProductAttributeValue == productSize);
    product.Variations?.forEach((x) => {
      if (x.ProductAttributeValue == productSize) {
        x.IsSelected = true;
      } else {
        x.IsSelected = false;
      }
    });

    let tempCartItem: ICartItems = {
      AvailableColors: product.AvailableColors,
      Id: product.Id + '_' + tempSelectedSize?.ProductAttributeValue,
      Pictures: product.ProductImageList,
      ProductId: product.Id,
      ProductName: product.Name,
      ProductSeName: product.SeName,
      Quantity: productQuantity,
      ProductSku: product.Sku,
      SubTotalValue: product.ProductPrice.Price,
      UnitPriceValue: product.ProductPrice.Price,
      UnitOldPrice: product.ProductPrice.OldPrice,
      Variations: product.Variations,
      Warnings: [],
      IsWishListed: product.IsWishListed,
      OrderMaximumQuantity: product.OrderMaximumQuantity,
      OrderMinimumQuantity: product.OrderMinimumQuantity,
      IsGiftVoucher: product.IsGiftVoucher,
      shouldOpenCart,
      shouldOpenNotification,
      Brand: product.ProductBrand?.Name,
      Category: product.CategoryName,
      Gender: product.Gender,
    };

    mutateAddToCart(tempCartItem);
  };
  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '-10px',
    threshold: 0.0,
  });

  const getCoords = (elem: any) => {
    // crossbrowser version
    var box = elem.getBoundingClientRect();

    var body = document.body;
    var docEl = document.documentElement;

    var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
    var scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

    var clientTop = docEl.clientTop || body.clientTop || 0;
    var clientLeft = docEl.clientLeft || body.clientLeft || 0;

    var top = box.top + scrollTop - clientTop;
    var left = box.left + scrollLeft - clientLeft;
    return { top: Math.round(top), left: Math.round(left) };
  };

  const onProductSizeChange = (size: number) => {
    setProductQuantity(1);
    selectProductSize(size);
  };
  useEffect(() => {
    if (productSize !== -1) setSelectedSize(productSize.toString());
  }, [productSize]);

  useEffect(() => {
    selectProductSize(product?.IsGiftVoucher ? product?.Variations[0].ProductAttributeValue : -1);
  }, [product]);

  useEffect(() => {
    if (!isVisible && scrolledDownFormAddToCart) {
      document.body.classList.add('floating-cart-visible');
    } else {
      document.body.classList.remove('floating-cart-visible');
    }
  }, [isVisible]);

  useEffect(() => {
    let scrollableContainer: any,
      scrollTop = 'scrollTop';

    if (typeof window !== 'undefined') {
      const el = document.getElementById('add-to-cart-button-container');

      const config = () => {
        if (window.innerWidth < 768) {
          scrollableContainer = window;
          scrollTop = 'scrollY';
        } else {
          scrollableContainer = document.body;
          scrollTop = 'scrollTop';
        }

        if (scrollableContainer) {
          const scrollFn = () => {
            const { top } = getCoords(el);
            setScrolledDownFormAddToCart(scrollableContainer[scrollTop] > top);
          };

          scrollableContainer.removeEventListener('scroll', scrollFn);
          scrollableContainer.addEventListener('scroll', scrollFn);
        }
      };

      config();
      window.addEventListener('resize', config);
    }
  }, []);

  return (
    <>
      <PageHeading className="mb-2 leading-tight">{product.Name}</PageHeading>
      <div className="mb-2 text-lg ">
        SKU: {product.Sku}
        <>{product.ProductStyles?.length ? ', Style: ' + product.ProductStyles.join(', ') : ''}</>
      </div>
      <div className="price text-2xl">
        {Number(product?.ProductPrice?.OldPrice) > Number(product?.ProductPrice?.Price) ? (
          <>
            <span className="text-gray-400 line-through">
              <span className="currency">৳</span>
              {numberFormat(product?.ProductPrice?.OldPrice)}
            </span>
            <span className="ml-4 whitespace-nowrap font-bold text-gray-600">
              <span className="currency">৳</span>
              {numberFormat(product?.ProductPrice?.Price)}
            </span>
            {/* <span className="ml-2 text-red-500">
              {calculateDiscountPercentage(Number(product?.ProductPrice?.Price), Number(product?.ProductPrice?.OldPrice))}
            </span> */}
          </>
        ) : (
          <span className="mr-4 font-bold text-gray-600">
            <span className="currency">৳</span>
            {numberFormat(product?.ProductPrice?.Price)}
          </span>
        )}
      </div>
      <div className="my-4">
        <div className="flex flex-wrap gap-2 text-xl">
          {product?.AvailableColors?.map((color) => (
            <div
              className={`group relative aspect-square w-[70px] overflow-hidden border !border-solid ${
                color?.OwnColor ? 'border-gray-400 ' : 'border-gray-200 opacity-80 '
              }`}
              key={color.ColorName}
            >
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-70 p-2 font-semibold text-white opacity-0 drop-shadow-[0_0_300px_#000] transition-opacity group-hover:opacity-100">
                <span className="text-center text-lg font-medium leading-tight">{color.ColorName}</span>
              </div>
              <Image
                className={`inline cursor-pointer object-cover`}
                src={color?.ProductDefaultImageUrl || '/images/product_default_img.png'}
                alt={color?.ProductSeName || ''}
                fill
                quality={100}
                onClick={() => {
                  if (!quickView) router.push(`${color?.ProductSeName}`);
                  if (quickView) {
                    setProductCardSeName && setProductCardSeName(color.ProductSeName);
                  }
                }}
                placeholder="blur"
                blurDataURL={base64Image}
                unoptimized={true}
              ></Image>
            </div>
          ))}
        </div>
      </div>
      {!product?.IsGiftVoucher && (
        <ProductSizes
          product={product}
          productId={product?.Id}
          captchaId={product?.CaptchaPublicKey}
          sizes={product.Variations}
          selectedSize={productSize}
          onSelect={onProductSizeChange}
        ></ProductSizes>
      )}

      <div className="product-quantity my-4  flex items-center">
        <div className="mb-2 mr-3 text-h5 font-bold">Quantity</div>
        <div className="duration-30 relative flex h-12 w-36 cursor-pointer items-center justify-center overflow-hidden rounded-[5px] border border-gray-400  text-xl transition-all ">
          <button
            onClick={() => {
              if (productQuantity > 1 && productSize !== -1) {
                setProductQuantity(productQuantity - 1);
              }
            }}
            className="!active:scale-100"
          >
            <IconMinus className={`${productQuantity > 1 && productSize !== -1 ? ' text-black' : 'text-gray-400'} w-3`} />
          </button>
          <span className="text-xl"> {productQuantity}</span>
          <button
            onClick={() => {
              if (
                productSize !== -1 &&
                productQuantity < (product.Variations?.find((x) => +x.ProductAttributeValue === +productSize)?.StockQuantity || 0)
              ) {
                setProductQuantity(productQuantity + 1);
              } else if (productSize === -1) {
                openNotification('danger', 'Error', 'Please select size.');
              } else if (productQuantity == product.Variations?.find((x) => +x.ProductAttributeValue === +productSize)?.StockQuantity) {
                openNotification('danger', 'Error', 'Product quantity exceeded stock on hand');
              }
            }}
            className="!active:scale-100"
          >
            <IconPlus
              className={`${
                productSize !== -1 &&
                productQuantity < (product.Variations?.find((x) => +x.ProductAttributeValue === +productSize)?.StockQuantity || 0)
                  ? ' text-black'
                  : 'text-gray-400'
              } w-3`}
            />
          </button>
        </div>
      </div>

      <div className="mt-5 flex">
        {product.ProductAdditional?.SizeGuide === '' ? (
          <Tooltip label="Not Available" key="sizeGuide">
            <span className="mr-4 text-xl opacity-60">
              <IconScale className="w-7" />
              <span className="ml-2">Size guide</span>
            </span>
          </Tooltip>
        ) : (
          <span className="a mr-4 flex select-none items-center text-xl" onClick={() => setSizeModal(true)}>
            <IconScale className="w-7" />
            <span className="underline-custom ml-2">Size guide</span>
          </span>
        )}
        {product.ProductAdditional?.CareInstruction === '' ? (
          <Tooltip label="Not Available" key="careGuide">
            <span className="mr-4 text-xl opacity-60">
              <IconCare className="w-7" />
              <span className="ml-2">Care guide</span>
            </span>
          </Tooltip>
        ) : (
          <span className="a ml-4 flex select-none items-center text-xl" onClick={() => setCareModal(true)}>
            <IconCare className="w-7" />
            <span className="underline-custom ml-2">Care guide</span>
          </span>
        )}
      </div>
      <div className="mt-5 border border-gray-400 p-3">
        <div className="mb-1 flex pl-6 text-start text-lg">
          <div className="mr-2 -ml-6 min-w-[18px] text-center">
            <IconInfoGraphic className="h-[22.4px] w-5 text-green-600" />
          </div>
          <div>
            <strong className="mb-2">Viewed: </strong> {numberFormat(productInfoGraphic)} people recently VIEWED this product.
          </div>
        </div>
        <div className="flex pl-6 text-start text-lg">
          <div className="mr-2 -ml-6 min-w-[18px] text-center">
            <IconFire className="-mt-1 w-4" />
          </div>
          <div>
            <strong className="mb-2">Popular: </strong> {numberFormat(product?.ProductPopularity)} people have BOUGHT this product.
          </div>
        </div>
      </div>
      <div className="mt-5 space-y-2" id="add-to-cart-button-container">
        <div className="action-button-container" ref={containerRef}>
          {productSize !== -1 ? (
            <div className="flex gap-4">
              <button
                id="buy-now"
                className={'flex w-1/2 flex-grow items-center justify-center bg-black text-xl text-white'}
                onClick={() => {
                  if (productSize != -1) {
                    const skuCheck = cartData?.Items?.find((item) => item.ProductSku === product?.Sku)?.Id;
                    let sizeCheck = null;
                    let breakParentLoop = false;
                    if (cartData?.Items) {
                      for (let i = 0; i < cartData?.Items?.length; i++) {
                        if (cartData?.Items[i]?.ProductSku === product.Sku) {
                          for (let j = 0; j < cartData?.Items[i]?.Variations.length; j++) {
                            if (cartData?.Items[i]?.Variations[j].IsSelected) {
                              if (cartData?.Items[i]?.Variations[j].ProductAttributeValue == productSize) {
                                sizeCheck = productSize;
                                breakParentLoop = true;
                                break;
                              }
                            }
                          }
                        }
                        if (breakParentLoop) {
                          break;
                        }
                      }
                    }
                    if (!skuCheck || !sizeCheck) {
                      addToCart(product, false);
                    } else {
                      router.push('/checkout');
                    }
                    setIsBuyNow(true);
                    let obj: { [key: string]: string } = {};

                    if (product?.CategoryName) {
                      let splittedCategories = product?.CategoryName.split(',');
                      splittedCategories.forEach((category, categoryIndex) => {
                        obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
                      });
                    }
                    if (!product?.IsGiftVoucher && product?.Gender) {
                      obj['gender'] = product?.Gender;
                    }
                    dataLayer.push({ ecommerce: null });
                    dataLayer.push({
                      event: 'begin_checkout',
                      ecommerce: {
                        currency: 'BDT',
                        value: product?.ProductPrice?.Price,
                        items: [
                          {
                            item_id: product?.SeName,
                            item_name: product?.Name,
                            item_sku: product?.Sku,
                            price: product?.ProductPrice?.Price,
                            quantity: productQuantity,
                            item_variant: product.Variations?.find((x) => x.IsSelected)?.VariantSku,
                            color: product?.AvailableColors?.find((color) => color.OwnColor)?.ColorName,
                            size: product.Variations?.find((x) => x.IsSelected)?.ProductAttributeValue,
                            item_brand: product?.ProductBrand?.Name,
                            ...obj,
                          },
                        ],
                      },
                    });
                  } else {
                    openNotification('danger', 'Purchase', 'Please select a size first.');
                  }
                }}
              >
                <span>Buy Now</span>
                <IconArrowUp className="ml-2 w-3.5 rotate-90" />
              </button>
              <button
                className="circle flex-grow-1 flex w-1/2 items-center justify-center border-2 border-black text-xl transition duration-200 ease-linear hover:bg-black hover:text-white"
                onClick={() => {
                  if (productSize !== -1) {
                    addToCart(product, false, true);
                  } else {
                    openNotification('danger', 'Cart', 'Please select a size first.');
                  }
                }}
              >
                Add To Cart
                <IconShoppingBag className={'ml-2 w-6' + (productSize === -1 ? ' text-gray-400' : '')} />
              </button>
            </div>
          ) : (
            <div>
              <button
                className={'flex h-[42px] w-full flex-grow items-center justify-center bg-black text-xl text-white'}
                onClick={() => {
                  if (productSize == -1) {
                    openNotification('success', 'Product Size', 'Please select a size first.');
                  }
                }}
              >
                <span>Select Size</span>
              </button>
            </div>
          )}
        </div>

        <button
          className="flex w-full items-center justify-center border-2 border-black text-xl transition duration-150 ease-linear hover:bg-black hover:text-white"
          onClick={() => {
            handleWishlistClick();
          }}
        >
          <span>{wishListProduct?.IsWishListed ? 'Remove from Wishlist' : 'Add to Wishlist'}</span>
          <IconHeart className="ml-2 w-6" filled={wishListProduct?.IsWishListed} />
        </button>
      </div>
      {!isVisible && scrolledDownFormAddToCart && !quickView && !cartPanel && (
        <FloatingCart
          product={product}
          addToCart={addToCart}
          productSize={productSize}
          selectProductSize={onProductSizeChange}
          setProductQuantity={setProductQuantity}
          productQuantity={productQuantity}
          setSelectedSize={setSelectedSize}
          selectedSize={selectedSize}
        />
      )}
      <Modal heading="Size guide" open={sizeModal} closeModal={() => setSizeModal(false)} changeHeight={false} className="w-full !p-1 md:w-[50rem]">
        <div className="relative aspect-square">
          <Image
            src={product.ProductAdditional?.SizeGuide}
            alt="Size guide"
            fill
            quality={100}
            sizes="100%"
            className="object-contain"
            placeholder="blur"
            blurDataURL={base64Image}
            unoptimized={true}
          ></Image>
        </div>
      </Modal>
      {product.ProductAdditional?.CareInstruction && (
        <Modal heading="Care guide" open={careModal} closeModal={() => setCareModal(false)} className="  w-full !p-1 md:w-[50rem]">
          <div className="relative aspect-square">
            <Image
              src={product.ProductAdditional?.CareInstruction}
              alt="Care guide"
              fill
              quality={100}
              sizes="100%"
              className="object-contain"
              placeholder="blur"
              blurDataURL={base64Image}
              unoptimized={true}
            ></Image>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ProductOptions;
